import styled from "styled-components";


export const Container = styled.footer`
  /* Glassmorphism background */
  background: linear-gradient(135deg, 
    rgba(30, 41, 59, 0.95) 0%, 
    rgba(15, 23, 42, 0.98) 50%,
    rgba(30, 41, 59, 0.95) 100%
  );
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(99, 102, 241, 0.2);
  padding: 5rem 15rem;
  margin-top: 15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  
  /* Animated gradient background */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
    animation: rotateBackground 20s linear infinite;
  }
  
  @keyframes rotateBackground {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* Floating particles */
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(circle, rgba(99, 102, 241, 0.3) 1px, transparent 1px),
      radial-gradient(circle, rgba(139, 92, 246, 0.2) 1px, transparent 1px);
    background-size: 50px 50px, 80px 80px;
    background-position: 0 0, 40px 40px;
    opacity: 0.3;
    animation: floatParticles 30s linear infinite;
  }
  
  @keyframes floatParticles {
    0% { transform: translateY(0); }
    100% { transform: translateY(-50px); }
  }
  
  /* Content wrapper with z-index */
  > * {
    position: relative;
    z-index: 1;
  }

  .logo{
    font-size: 3.2rem;
    font-weight: 700;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    text-shadow: 0 0 30px rgba(99, 102, 241, 0.5);
    
    &:hover {
      transform: translateY(-5px) scale(1.05);
      filter: drop-shadow(0 8px 16px rgba(99, 102, 241, 0.4));
    }
  }

  p{
    letter-spacing: 0.2rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.6rem;
    color: var(--text-light);
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--primary);
      transform: translateY(-2px);
    }
    
    img{
      width: 3rem;
      height: 3rem;
      animation: spinning3D 8s infinite ease-in-out;
      filter: drop-shadow(0 4px 12px rgba(99, 102, 241, 0.4));
      transition: all 0.3s ease;
      
      &:hover {
        animation-play-state: paused;
        transform: scale(1.2) rotateY(180deg);
      }
    }
  }
  
  .social-media{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;

    a {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 5rem;
      height: 5rem;
      border-radius: 1.2rem;
      background: rgba(99, 102, 241, 0.1);
      border: 1px solid rgba(99, 102, 241, 0.3);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      perspective: 1000px;
      
      /* Glassmorphism effect */
      backdrop-filter: blur(10px);
      
      /* Gradient border on hover */
      &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 1.2rem;
        padding: 2px;
        background: var(--gradient-primary);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        opacity: 0;
        transition: opacity 0.4s ease;
      }
      
      /* Glow effect */
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 1.2rem;
        background: var(--gradient-primary);
        opacity: 0;
        filter: blur(20px);
        transition: opacity 0.4s ease;
        z-index: -1;
      }
      
      &:hover {
        transform: translateY(-8px) rotateY(10deg) rotateX(-5deg) scale(1.1);
        background: rgba(99, 102, 241, 0.2);
        border-color: var(--primary);
        box-shadow: 0 12px 40px rgba(99, 102, 241, 0.5);
        
        &::before {
          opacity: 1;
        }
        
        &::after {
          opacity: 0.6;
        }
        
        img {
          transform: scale(1.1) rotateZ(5deg);
          filter: brightness(1.3) drop-shadow(0 4px 12px rgba(99, 102, 241, 0.6));
        }
      }
      
      &:active {
        transform: translateY(-4px) scale(1.05);
      }
      
      /* Unique colors for each social */
      &:nth-child(1):hover { /* LinkedIn */
        box-shadow: 0 12px 40px rgba(14, 165, 233, 0.5);
        border-color: #0ea5e9;
      }
      
      &:nth-child(2):hover { /* Kaggle */
        box-shadow: 0 12px 40px rgba(34, 197, 255, 0.5);
        border-color: #22c5ff;
      }
      
      &:nth-child(3):hover { /* GitHub */
        box-shadow: 0 12px 40px rgba(139, 92, 246, 0.5);
        border-color: #8b5cf6;
      }
      
      &:nth-child(4):hover { /* Scholar */
        box-shadow: 0 12px 40px rgba(59, 130, 246, 0.5);
        border-color: #3b82f6;
      }
      
      &:nth-child(5):hover { /* Telegram */
        box-shadow: 0 12px 40px rgba(6, 182, 212, 0.5);
        border-color: #06b6d4;
      }
      
      &:nth-child(6):hover { /* Instagram */
        box-shadow: 0 12px 40px rgba(236, 72, 153, 0.5);
        border-color: #ec4899;
      }
      
      img {
        width: 2.8rem;
        height: 2.8rem;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        filter: brightness(1.1) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3));
      }
    }
  }

  @keyframes spinning3D {
    0%, 100% { 
      transform: rotateY(0deg) rotateX(0deg);
    }
    25% { 
      transform: rotateY(180deg) rotateX(10deg);
    }
    50% { 
      transform: rotateY(360deg) rotateX(0deg);
    }
    75% { 
      transform: rotateY(180deg) rotateX(-10deg);
    }
  }

  @media(max-width: 1200px){
    padding: 4rem 8rem;
  }

  @media(max-width: 800px){
    padding: 4rem 4rem;
    flex-direction: column;
    gap: 3rem;
    text-align: center;
    
    .social-media {
      flex-wrap: wrap;
      justify-content: center;
    }
  }
  
  @media(max-width: 600px){
    padding: 4rem 2rem;
    margin-top: 10rem;
    
    .logo {
      font-size: 2.4rem;
    }
    
    p{
      font-size: 1.3rem;
      flex-direction: column;
      
      img {
        width: 2.4rem;
        height: 2.4rem;
      }
    }
    
    .social-media {
      gap: 1rem;
      
      a {
        width: 4.5rem;
        height: 4.5rem;
        
        img {
          width: 2.4rem;
          height: 2.4rem;
        }
      }
    }
  }
  
  @media(max-width: 400px){
    .social-media {
      a {
        width: 4rem;
        height: 4rem;
        
        img {
          width: 2rem;
          height: 2rem;
        }
      }
    }
  }
`